import { DataMapperMappingTypes } from '../../enums/data-mapper-mapping-types';
import { IMappingField } from '../../interfaces/data-mapper/mapping-field';
import { Guid } from '../guid';

/**
 * Describes a single field path within a DataMapper transformation — used for both
 * source and destination sides. The path is expressed as an ordered array of
 * `mappingFields` segments that together form a dot-notation field reference.
 */
export class Mapping {
	/** Unique identifier for this mapping. */
	id = Guid();

	/**
	 * Ordered array of path segments forming the field reference.
	 * E.g. `user.address.city` → `[{objectKey:'user'}, {objectKey:'address'}, {objectKey:'city'}]`.
	 * Set `isArray: true` on a segment when that level is an array.
	 */
	mappingFields: IMappingField[] = [];

	/**
	 * The target data type for the value at this path.
	 * Used for type coercion during mapping.
	 * @default DataMapperMappingTypes.string
	 */
	dataType: DataMapperMappingTypes = DataMapperMappingTypes.string;

	/** Optional sample/preview value for UI display. Populated from `get_flow_data_context` where available. */
	sampleData?: any;

	/**
	 * Defines the repeat source behaviour for FreeText special nodes.
	 * Only relevant when `mappingFields[0].specialNode === 'FreeText'`.
	 * Use `'_once'` to evaluate the FreeText value once; any other string repeats relative to that field.
	 */
	repeatSource?: string;

	/** When `true`, this mapping is skipped entirely at runtime. */
	disabled?: boolean;

	/**
	 * When `true`, null source values are written to the destination.
	 * When `false` (default), null values are skipped and the destination field is left unset.
	 * @default false
	 */
	mapNullValues?: boolean;

	constructor(init?: Partial<Mapping>) {
		if (init) {
			Object.assign(this, init);
		}
	}
}
